import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

import * as TYPES from './types'
import * as ROUTES from './../../routes'
import { setPageMeta, resetPageMeta, setJsonLd } from '../../utils/seo'

import PublicHeader from '../../Components/Headers/PublicHeader';
import PublicFooter from '../../Components/Footers/PublicFooter';
import PublicContainer from '../../Components/PublicContainer';

const BASE = window.location.origin + '/assets/landing';

const screenshotCrashGrouping        = `${BASE}/gamenostic-CrashGrouping.jpg`;
const screenshotCrashDetailsDeepDive = `${BASE}/gamenostic-CrashDetailsDeepDive.jpg`;
const screenshotPlayerFlowMapping    = `${BASE}/gamenostic-PlayerFlowMapping.jpg`;
const screenshotSlackInstantAlerts   = `${BASE}/gamenostic-SlackInstantAlerts.jpg`;
const screenshotDiscordInstantAlerts = `${BASE}/gamenostic-DiscordInstantAlerts.jpg`;

const screenshotUEPluginsSetup = `${BASE}/gamenostic-UEPluginsSetup.jpg`;
const screenshotConsolesPCMobileBG = `${BASE}/gamenostic-ConsolesPCMobileBG.jpg`;
const screenshotConsolesPCMobileLabels = `${BASE}/gamenostic-ConsolesPCMobileLabels.png`;

const seo_images_gallery = [
    { label: 'Crashes AI Grouping',    image: screenshotCrashGrouping,         seoAlt: 'AI-powered crash grouping — automatic crash deduplication and analysis for Unreal Engine' },
    { label: 'Crash Details',          image: screenshotCrashDetailsDeepDive,  seoAlt: 'Crash details deep dive — full stack trace and call stack for Unreal Engine crashes' },
    { label: 'Locations Flow',         image: screenshotPlayerFlowMapping,     seoAlt: 'Player flow mapping — real-time session geo analytics and location tracking for Unreal Engine' },
    { label: 'Slack Alerts',           image: screenshotSlackInstantAlerts,    seoAlt: 'Slack crash alerts — instant Unreal Engine crash notifications delivered to Slack channels' },
    { label: 'Discord Alerts',         image: screenshotDiscordInstantAlerts,  seoAlt: 'Discord crash alerts — instant Unreal Engine crash notifications delivered to Discord servers' },
];

const Landing: React.FC<TYPES.LandingProps> = () => {
    const canvasRef = useRef<HTMLCanvasElement>(null);

    const scrollTo = (id: string) => (e: React.MouseEvent) => {
        e.preventDefault();
        const el = document.getElementById(id);
        if (el) {
            const top = el.getBoundingClientRect().top + window.scrollY - 55;
            window.scrollTo({ top, behavior: 'smooth' });
        }
    };

    // Scroll to top only on initial navigation to this page
    useEffect(() => {
        window.scrollTo(0, 0);
        setPageMeta(
            'GameNostic — Crash Analytics & Monitoring for Unreal Engine | No SDKs. 5 Min Setup.',
            'The crash reporting tool built for Unreal Engine. Catch 99% of crashes on PC, PS5, Xbox, Switch & mobile. No native SDKs. No pipeline changes. Real-time session monitoring, AI crash grouping, Slack & Discord alerts. Free during beta.',
            'https://gamenostic.gg/',
            'https://gamenostic.gg/assets/landing/gamenostic-CrashDetailsDeepDive.jpg'
        );
        setJsonLd({
            '@context': 'https://schema.org',
            '@graph': [
                {
                    '@type': 'FAQPage',
                    mainEntity: [
                        {
                            '@type': 'Question',
                            name: 'Does GameNostic require native SDKs for console crash reporting?',
                            acceptedAnswer: { '@type': 'Answer', text: 'No. GameNostic requires zero native SDKs. If Unreal Engine compiles on a platform — PC, PS5, Xbox, Nintendo Switch, or mobile — GameNostic runs on it without any platform-specific code or SDK access.' },
                        },
                        {
                            '@type': 'Question',
                            name: 'How is GameNostic different from Sentry for Unreal Engine?',
                            acceptedAnswer: { '@type': 'Answer', text: "GameNostic is built specifically for Unreal Engine using FCoreDelegates — the official UE extension point. It requires no Crashpad handler, no symbol upload pipeline, and no CI changes. Sentry uses Crashpad which is mutually exclusive with UE's CrashReportClient. GameNostic also provides real-time session monitoring, client/server/editor crash separation, and automatic ensure capture — features not available in Sentry's UE SDK." },
                        },
                        {
                            '@type': 'Question',
                            name: 'How long does it take to set up GameNostic in an Unreal Engine project?',
                            acceptedAnswer: { '@type': 'Answer', text: 'GameNostic is designed to be running in under 5 minutes. Drop in the plugin, paste your credentials, done. No CMake, no CI changes, no native SDK wrangling, and no build pipeline modifications required.' },
                        },
                        {
                            '@type': 'Question',
                            name: 'Does GameNostic work with Unreal Engine 5?',
                            acceptedAnswer: { '@type': 'Answer', text: "Yes. GameNostic is built for Unreal Engine and works with UE5. It integrates using Unreal's official extension points including FCoreDelegates and OnHandleSystemEnsure, and captures crashes, ensures, and pre-crash events across all UE5 platforms." },
                        },
                        {
                            '@type': 'Question',
                            name: 'Is GameNostic a Sentry, BugSplat, or Backtrace alternative?',
                            acceptedAnswer: { '@type': 'Answer', text: "GameNostic is a purpose-built alternative to Sentry, BugSplat, and Backtrace specifically for Unreal Engine teams. Unlike those tools, GameNostic requires no symbol upload step, no native SDK per console platform, no Crashpad handler, and no pipeline changes. It also adds live session monitoring, real-time geo analytics, and AI-powered crash grouping that the others don't offer." },
                        },
                    ],
                },
                {
                    '@type': 'ImageGallery',
                    '@id': 'https://gamenostic.gg/#dashboard-screenshots',
                    name: 'GameNostic Dashboard Screenshots — Unreal Engine Crash Analytics',
                    description: 'Screenshots of the GameNostic dashboard showing crash analytics, AI crash grouping, player flow mapping, and real-time Slack & Discord alerts for Unreal Engine games.',
                    url: 'https://gamenostic.gg/#dashboard',
                    image: seo_images_gallery.map(gallery_image => ({
                        '@type': 'ImageObject',
                        contentUrl: 'https://gamenostic.gg/assets/landing/' + gallery_image.image.split('/assets/landing/')[1],
                        url: 'https://gamenostic.gg/#dashboard',
                        name: gallery_image.label,
                        description: gallery_image.seoAlt,
                        inLanguage: 'en',
                        encodingFormat: 'image/jpeg',
                    })),
                },
            ],
        });
        return () => resetPageMeta();
    }, []);

    // Scroll-reveal via IntersectionObserver
    useEffect(() => {
        const reveals = document.querySelectorAll<HTMLElement>('.gn-public .reveal');
        const observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    entry.target.classList.add('visible');
                    observer.unobserve(entry.target);
                }
            });
        }, { threshold: 0.08 });
        reveals.forEach(el => observer.observe(el));
        return () => observer.disconnect();
    }, []);

    // landing canvas animation
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        let rafId: number;
        let mounted = true;

        const LOG_LINES = [
            'LogCrashContext: Error: Ensure condition failed: IsValid(Actor)',
            'LogOutputDevice: Warning: GC triggered during crash handler',
            'LogWindows: Error: Access violation - code c0000005',
            'UE_LOG(LogTemp, Error, TEXT("NullPtr in BeginPlay"))',
            'LogRHI: Fatal: Unhandled exception: EXCEPTION_STACK_OVERFLOW',
            'LogCrash: FCoreDelegates::OnHandleSystemEnsure',
            'LogNet: Error: Client timeout — session lost',
            'LogEngine: Warning: Memory pool exceeded threshold',
            'LogCrashContext: CrashGUID: UE4CC-Windows-A3F2',
            'LogCrash: PreCrashEvent captured — uploading session',
            'LogStreaming: Error: Async load failed: /Game/Maps/Level_03',
            'UE_LOG(LogGN, Log, TEXT("Session start: user_8821"))',
            'LogPhysics: Warning: Rigid body penetration depth exceeded',
            'LogAnimation: Error: Missing bone reference in skeleton',
            'LogCrash: Stack trace captured — 14 frames',
            'LogRHI: CreateTexture2D failed — out of VRAM',
            'LogNet: PacketLoss=0.23 RTT=340ms — degraded',
            'LogCrashContext: IsEnsure=1 IsAssert=0',
            'LogTemp: player_location=(X=1240.0,Y=-880.5,Z=102.0)',
            'LogCrash: FullSessionLog uploaded — 2.4MB',
        ];

        interface Col {
            x: number; y: number; speed: number; opacity: number;
            fontSize: number; gap: number; lines: { text: string; alpha: number }[];
        }
        let cols: Col[] = [];

        function resize() {
            canvas!.width = canvas!.offsetWidth;
            canvas!.height = canvas!.offsetHeight;
            const colWidth = 420;
            const count = Math.ceil(canvas!.width / colWidth) + 1;
            cols = [];
            for (let i = 0; i < count; i++) {
                const li = Math.floor(Math.random() * LOG_LINES.length);
                const col: Col = {
                    x: i * colWidth + Math.random() * 60 - 30,
                    y: Math.random() * -canvas!.height,
                    speed: 0.35 + Math.random() * 0.45,
                    opacity: 0.18 + Math.random() * 0.22,
                    fontSize: 10 + Math.floor(Math.random() * 3),
                    gap: 22 + Math.floor(Math.random() * 8),
                    lines: [],
                };
                for (let j = 0; j < 30; j++) {
                    col.lines.push({ text: LOG_LINES[(li + j) % LOG_LINES.length], alpha: Math.max(0, 1 - j * 0.07) });
                }
                cols.push(col);
            }
        }

        function draw() {
            if (!mounted) return;
            ctx!.clearRect(0, 0, canvas!.width, canvas!.height);
            cols.forEach(col => {
                col.y += col.speed;
                if (col.y > canvas!.height + 200) col.y = -col.gap * col.lines.length;
                col.lines.forEach((line, j) => {
                    const y = col.y + j * col.gap;
                    if (y < -col.gap || y > canvas!.height + col.gap) return;
                    const edgeFade = Math.min(Math.min(y / 120, 1), Math.min((canvas!.height - y) / 180, 1));
                    ctx!.font = col.fontSize + 'px "DM Mono", monospace';
                    ctx!.fillStyle = j === 0
                        ? `rgba(255,80,25,${col.opacity * line.alpha * edgeFade})`
                        : `rgba(26,24,37,${col.opacity * line.alpha * edgeFade * 0.85})`;
                    ctx!.fillText(line.text, col.x, y);
                });
            });
            rafId = requestAnimationFrame(draw);
        }

        window.addEventListener('resize', resize);
        resize();
        draw();

        return () => {
            mounted = false;
            cancelAnimationFrame(rafId);
            window.removeEventListener('resize', resize);
        };
    }, []);

    return (
        <PublicContainer>
                <PublicHeader showEALink showLogin>
                    <ul className="gn-header-nav-links">
                        <li><a href="#session" onClick={scrollTo('session')}>Session</a></li>
                        <li><a href="#visibility" onClick={scrollTo('visibility')}>Visibility</a></li>
                        <li><a href="#setup" onClick={scrollTo('setup')}>Setup</a></li>
                        <li><a href="#platform" onClick={scrollTo('platform')}>Platforms</a></li>
                    </ul>
                </PublicHeader>

                {/* ── Visit Card ── */}
                <section className="visit-card">
                    <canvas ref={canvasRef} id="gn-landing-canvas" />
                    <div className="visit-card-content">
                        <div className="visit-card-eyebrow">DESIGNED FOR LIGHTNING FAST SETUP</div>
                        <h1>
                            Real-Time Crash Catcher<br />
                            <em>Built for Unreal Engine</em>
                        </h1>
                        <p className="visit-card-sub">
                            The crash catcher built for every UE platform: PC, console, and mobile. No native SDKs. No pipeline changes. Catches the crashes the UE crash reporter misses.
                        </p>
                        <div className="visit-card-actions">
                            <Link className="gn-btn-primary" to={ROUTES.PATH.EARLY_ACCESS}>Get Early Access</Link>
                            <a href="#dashboard" className="gn-btn-secondary" onClick={scrollTo('session')}>See the features</a>
                        </div>
                        <div className="visit-card-tagline">— By gamedevs. For gamedevs.</div>
                    </div>
                </section>

                <div className="section-divider" />

                {/* ── 01 Full Session Picture ── */}
                <section id="session">
                    <div className="chapter reveal" style={{ paddingTop: '80px' }}>
                        <span className="chapter-label">01 — The Full Session Picture</span>
                        <h2>From first frame<br />to crash.</h2>
                        <p>The crash is a symptom. Session analytics reveals the diagnosis.</p>
                    </div>
                    <div className="screenshot-single reveal visible">
                        <div className="screenshot-wrap">
                            <img 
                                src={screenshotCrashDetailsDeepDive} 
                                alt="Crash details deep dive — full stack trace and call stack for Unreal Engine crashes" 
                            />
                        </div>
                        <p className="screenshot-label">Crash Deep Dive</p>
                    </div>
                    <div className="feature-grid cols-4 reveal">
                        <div className="fcard">
                            <div className="fcard-icon">
                                <svg viewBox="0 0 24 24"><path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3"/><path d="M12 9v4"/><path d="M12 17h.01"/></svg>
                            </div>
                            <div className="fcard-label">Pre-crash events</div>
                            <h3>The snapshot before the crash.</h3>
                            <p>The Game Nostic plugin fires automatically before the engine goes down. You get the last known state — no extra code, no manual hooks.</p>
                        </div>
                        <div className="fcard">
                            <div className="fcard-icon">
                                <svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><path d="M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20"/><path d="M2 12h20"/></svg>
                            </div>
                            <div className="fcard-label">Geo analytics</div>
                            <h3>A live map of your testers' location.</h3>
                            <p>In today's remote-work world, see tester activity by location in real-time in the Game Nostic Dashboard. Know exactly where in the world things are breaking.</p>
                        </div>
                        <div className="fcard">
                            <div className="fcard-icon">
                                <svg viewBox="0 0 24 24"><circle cx="12" cy="4.5" r="2.5"/><path d="m10.2 6.3-3.9 3.9"/><circle cx="4.5" cy="12" r="2.5"/><path d="M7 12h10"/><circle cx="19.5" cy="12" r="2.5"/><path d="m13.8 17.7 3.9-3.9"/><circle cx="12" cy="19.5" r="2.5"/></svg>
                            </div>
                            <div className="fcard-label">Map-by-map flow</div>
                            <h3>Review the path. Not just the crash.</h3>
                            <p>Connect → lobby → Level_01 → crash. Each map a user loaded in their session is displayed in the Game Nostic. Track what maps loaded before the crash for useful insights.</p>
                        </div>
                        <div className="fcard">
                            <div className="fcard-icon">
                                <svg viewBox="0 0 24 24"><path d="M15 12h-5"/><path d="M15 8h-5"/><path d="M19 17V5a2 2 0 0 0-2-2H4"/><path d="M8 21h12a2 2 0 0 0 2-2v-1a1 1 0 0 0-1-1H11a1 1 0 0 0-1 1v1a2 2 0 1 1-4 0V5a2 2 0 1 0-4 0v2a1 1 0 0 0 1 1h3"/></svg>
                            </div>
                            <div className="fcard-label">Complete session log</div>
                            <h3>Every UE_LOG. From launch to crash.</h3>
                            <p>With every crash, ensure, or assert event, Game Nostic automatically captures an admin-defined set lines from the player's UE log (up to 200). Full log upload is opt-in and user controlled.</p>
                        </div>
                    </div>
                    <div className="screenshot-pair reveal visible">
                        <div>
                            <div className="screenshot-wrap">
                                <img src={screenshotPlayerFlowMapping} alt="Player flow mapping — real-time session geo analytics and location tracking for Unreal Engine" />
                            </div>
                            <p className="screenshot-label">Player Flow Mapping</p>
                        </div>
                        <div>
                            <div className="screenshot-wrap">
                                <img src={screenshotCrashGrouping} alt="AI-powered crash grouping — automatic crash deduplication and analysis for Unreal Engine" />
                            </div>
                            <p className="screenshot-label">Crash Pattern Matching</p>
                        </div>
                    </div>
                </section>

                {/* ── 02 Team Visibility ── */}
                <section id="visibility">
                    <div className="chapter reveal" style={{ paddingTop: '70px' }}>
                        <span className="chapter-label">02 — Team Visibility</span>
                        <h2>Crash intel where your team works.</h2>
                        <p>Before anyone files a bug report, immediately get alerts in Slack, Discord, and your dashboard.</p>
                    </div>
                    <div className="screenshot-pair reveal visible">
                        <div>
                            <div className="screenshot-wrap">
                                <img src={screenshotSlackInstantAlerts} alt="Slack crash alerts — instant Unreal Engine crash notifications delivered to Slack channels" />
                            </div>
                            <p className="screenshot-label">Slack Instant Alerts</p>
                        </div>
                        <div>
                            <div className="screenshot-wrap">
                                <img src={screenshotDiscordInstantAlerts} alt="Discord crash alerts — instant Unreal Engine crash notifications delivered to Discord servers" />
                            </div>
                            <p className="screenshot-label">Discord Instant Alerts</p>
                        </div>
                    </div>
                    <div className="feature-grid reveal">
                        <div className="fcard">
                            <div className="fcard-icon">
                                <svg viewBox="0 0 24 24"><path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9"/><path d="M10.3 21a1.94 1.94 0 0 0 3.4 0"/><path d="M4 2C2.8 3.7 2 5.7 2 8"/><path d="M22 8c0-2.3-.8-4.3-2-6"/></svg>
                            </div>
                            <div className="fcard-label">Real-time notifications</div>
                            <h3>See crashes before anyone reports a thing.</h3>
                            <p>Instant crash alerts on Slack or Discord with key user info — plus a full dashboard for deeper investigation. No UE Crash Report "Send" button required. Ever.</p>
                        </div>
                        <div className="fcard">
                            <div className="fcard-icon">
                                <svg viewBox="0 0 24 24"><path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z"/><path d="m9 12 2 2 4-4"/></svg>
                            </div>
                            <div className="fcard-label">Catch 99% of crashes</div>
                            <h3>Stop driving with one eye blind.</h3>
                            <p>20% of your crashes in UE are not even making it to the UE Crash Reporter. Game Nostic integrates behind-the-scenes — capturing what the Unreal Engine misses.</p>
                        </div>
                        <div className="fcard">
                            <div className="fcard-icon">
                                <svg viewBox="0 0 24 24"><rect width="7" height="9" x="3" y="3" rx="1"/><rect width="7" height="5" x="14" y="3" rx="1"/><rect width="7" height="9" x="14" y="12" rx="1"/><rect width="7" height="5" x="3" y="16" rx="1"/></svg>
                            </div>
                            <div className="fcard-label">Client. Server. Editor.</div>
                            <h3>Separate reporting for all three.</h3>
                            <p>A client crash, server crash and editor crash are different problems. Game Nostic treats them that way with distinct designations and reporting. Game Nostic also makes getting server crash info painless with its auto reporting features.</p>
                        </div>
                    </div>
                </section>

                {/* ── 03 Plug In and Go ── */}
                <section id="setup">
                    <div className="chapter reveal" style={{ paddingTop: '70px' }}>
                        <span className="chapter-label">03 — Plug In and Go</span>
                        <h2>Running in<br />five minutes.</h2>
                        <p>Drop in the plugin. Paste your credentials. Done. No CMake. No CI changes. No native SDK wrangling.</p>
                    </div>
                    <div className="screenshot-bleed tall parallax-plugin reveal visible" id="parallax-plugin">
                        <img src={screenshotUEPluginsSetup} alt="GameNostic is the UE5 Plugin — Installed in minutes" />
                        <p className="screenshot-label">UE5 Plugin — Installed in minutes</p>
                    </div>
                    <div className="feature-grid reveal">
                        <div className="fcard">
                            <div className="fcard-icon">
                                <svg viewBox="0 0 24 24"><line x1="10" x2="14" y1="2" y2="2"/><line x1="12" x2="15" y1="14" y2="11"/><circle cx="12" cy="14" r="8"/></svg>
                            </div>
                            <div className="fcard-label">Setup time</div>
                            <h3>Running in 5 minutes.</h3>
                            <p>Drop in the plugin. Paste your credentials. Done. No CMake. No CI changes. No native SDK wrangling. Your build pipeline stays exactly as you left it.</p>
                        </div>
                        <div className="fcard">
                            <div className="fcard-icon">
                                <svg viewBox="0 0 24 24"><rect width="16" height="16" x="4" y="4" rx="2"/><rect width="6" height="6" x="9" y="9" rx="1"/><path d="M15 2v2M15 20v2M9 2v2M9 20v2M2 15h2M20 15h2M2 9h2M20 9h2"/></svg>
                            </div>
                            <div className="fcard-label">Engine integration</div>
                            <h3>Wired directly into UE.</h3>
                            <p>Game Nostic uses Unreal's official extension point. No conflicts with the engine. No mysterious double-crashes.</p>
                        </div>
                        <div className="fcard">
                            <div className="fcard-icon">
                                <svg viewBox="0 0 24 24"><line x1="6" x2="6" y1="3" y2="15"/><circle cx="18" cy="6" r="3"/><circle cx="6" cy="18" r="3"/><circle cx="6" cy="6" r="3"/><path d="M18 9a9 9 0 0 1-9 9"/></svg>
                            </div>
                            <div className="fcard-label">Build pipeline</div>
                            <h3>No pipeline changes. Not one.</h3>
                            <p>Game Nostic uses what UE already gives you. No #if PLATFORM_WINDOWS. No extra CI steps. It just works.</p>
                        </div>
                    </div>
                </section>

                {/* ── 04 Every Platform ── */}
                <section id="platform">
                    <div className="chapter reveal" style={{ paddingTop: '70px' }}>
                        <span className="chapter-label">04 — Every Platform</span>
                        <h2>PC. PS5. Xbox.<br />Switch. One dashboard.</h2>
                        <p>If Unreal compiles on it, Game Nostic runs on it. Zero platform-specific code. Zero native SDKs.</p>
                    </div>
                    <div className="screenshot-bleed parallax reveal visible" id="parallax-consoles">
                        <img className="parallax-bg" src={screenshotConsolesPCMobileBG} alt="GameNostic is the UE5 Plugin — Cross-platform support for PC, consoles, and mobile" />
                        <img className="parallax-logos" src={screenshotConsolesPCMobileLabels} alt="GameNostic is the UE5 Plugin — Cross-platform support for PC, consoles, and mobile" />
                        <p className="screenshot-label">PC · PS5 · Switch · Xbox · Mobile</p>
                    </div>
                    <div className="feature-grid cols-4 reveal">
                        <div className="fcard">
                            <div className="fcard-icon">
                                <svg viewBox="0 0 24 24"><path d="M18 8V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v7a2 2 0 0 0 2 2h8"/><path d="M10 19v-3.96 3.15"/><path d="M7 19h5"/><rect width="6" height="10" x="16" y="12" rx="2"/></svg>
                            </div>
                            <div className="fcard-label">Cross-platform</div>
                            <h3>One login. Complete visibility.</h3>
                            <p>Zero platform-specific code. Zero native SDKs. If Unreal compiles on it, Game Nostic runs on it — consoles included. No more juggling three platform portals.</p>
                        </div>
                        <div className="fcard">
                            <div className="fcard-icon">
                                <svg viewBox="0 0 24 24"><line x1="6" x2="10" y1="11" y2="11"/><line x1="8" x2="8" y1="9" y2="13"/><line x1="15" x2="15.01" y1="12" y2="12"/><line x1="17" x2="17.01" y1="10" y2="10"/><path d="M17.32 5H6.68a4 4 0 0 0-3.978 3.59c-.006.052-.01.101-.017.152C2.604 9.416 2 14.456 2 16a3 3 0 0 0 3 3c1 0 1.5-.5 2-1l1.414-1.414A2 2 0 0 1 9.828 16h4.344a2 2 0 0 1 1.414.586L17 18c.5.5 1 1 2 1a3 3 0 0 0 3-3c0-1.545-.604-6.584-.685-7.258-.007-.05-.011-.1-.017-.151A4 4 0 0 0 17.32 5z"/></svg>
                            </div>
                            <div className="fcard-label">Console studios</div>
                            <h3>Replace three siloed tools with one.</h3>
                            <p>PlayStation DevNet. Xbox Partner Center. Nintendo's crash portal. All delayed, all siloed, all manual. Game Nostic replaces the lot — unified, real-time, searchable.</p>
                        </div>
                        <div className="fcard">
                            <div className="fcard-icon">
                                <svg viewBox="0 0 24 24"><path d="M12 5a3 3 0 1 0-5.997.125 4 4 0 0 0-2.526 5.77 4 4 0 0 0 .556 6.588A4 4 0 1 0 12 18Z"/><path d="M9 13a4.5 4.5 0 0 0 3-4"/><path d="M12 13h4"/><path d="M12 18h6a2 2 0 0 1 2 2v1"/><path d="M12 8h8"/><path d="M16 8V5a2 2 0 0 1 2-2"/><circle cx="16" cy="13" r=".5"/><circle cx="18" cy="3" r=".5"/><circle cx="20" cy="21" r=".5"/><circle cx="20" cy="8" r=".5"/></svg>
                            </div>
                            <div className="fcard-label">AI crash grouping</div>
                            <h3>Crashes grouped by meaning.</h3>
                            <p>Use large language models for what they're good at: pattern recognition. Cluster crashes that look different on the surface but share the same root cause. Auto-generated summaries — no manual tuning required.</p>
                        </div>
                        <div className="fcard">
                            <div className="fcard-icon">
                                <svg viewBox="0 0 24 24"><path d="m8 2 1.88 1.88"/><path d="M14.12 3.88 16 2"/><path d="M9 7.13v-1a3.003 3.003 0 1 1 6 0v1"/><path d="M12 20c-3.3 0-6-2.7-6-6v-3a4 4 0 0 1 4-4h4a4 4 0 0 1 4 4v3c0 3.3-2.7 6-6 6z"/><path d="M12 20v-9"/><path d="M6.53 9C4.6 8.8 3 7.1 3 5"/><path d="M6 13H2"/><path d="M3 21c0-2.1 1.7-3.9 3.8-4"/><path d="M20.97 5c0 2.1-1.6 3.8-3.5 4"/><path d="M22 13h-4"/><path d="M17.2 17c2.1.1 3.8 1.9 3.8 4"/></svg>
                            </div>
                            <div className="fcard-label">Ensure tracking</div>
                            <h3>Ensures caught. Automatically.</h3>
                            <p>OnHandleSystemEnsure is hooked the moment you install. Every ensure, the near-crashes easily overlooked, tracked without writing a single line.</p>
                        </div>
                    </div>
                </section>

                {/* ── Pitch / Final CTA ── */}
                <section className="pitch reveal">
                    <h2>Drop one plugin. Get a clear picture.</h2>
                    <p>Crash monitoring, session tracking, and AI-grouped crash reports on PC, PS5, Xbox, Switch, and mobile. No native SDKs. No pipeline changes. Works in 5 minutes.</p>
                    <div className="pill-row">
                        <span className="pill">Zero native SDKs</span>
                        <span className="pill">Zero pipeline changes</span>
                        <span className="pill">Every UE platform</span>
                        <span className="pill">5-minute setup</span>
                        <span className="pill">AI crash grouping</span>
                    </div>
                    <Link className="gn-btn-primary" style={{ fontSize: '16px', padding: '16px 40px' }} to={ROUTES.PATH.EARLY_ACCESS}>Get Early Access</Link>
                </section>

                <PublicFooter/>
            </PublicContainer>
    );
};

export default Landing;
